import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { SiteProvider } from "@/context/SiteContext";
import { PublicLayout } from "@/components/PublicLayout";
import { Seo } from "@/components/Seo";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import ForClients from "@/pages/ForClients";
import ForFacilities from "@/pages/ForFacilities";
import ServiceAreas from "@/pages/ServiceAreas";
import ServiceAreaDetail from "@/pages/ServiceAreaDetail";
import Careers from "@/pages/Careers";
import JobDetail from "@/pages/JobDetail";
import JobApplication from "@/pages/JobApplication";
import Contact from "@/pages/Contact";
import BookRide from "@/pages/BookRide";
import PrivatePay from "@/pages/PrivatePay";
import OdpServices from "@/pages/OdpServices";
import Legal from "@/pages/Legal";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import Overview from "@/pages/admin/Overview";
import FollowUps from "@/pages/admin/FollowUps";
import Leads from "@/pages/admin/Leads";
import JobOpenings from "@/pages/admin/JobOpenings";
import TestimonialsAdmin from "@/pages/admin/TestimonialsAdmin";
import ServiceAreasAdmin from "@/pages/admin/ServiceAreasAdmin";
import MediaLibrary from "@/pages/admin/MediaLibrary";
import Announcements from "@/pages/admin/Announcements";
import SiteContent from "@/pages/admin/SiteContent";

const P = ({ title, description, children }) => (
  <PublicLayout>
    <Seo title={title} description={description} />
    {children}
  </PublicLayout>
);

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <SiteProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<P title="CareDeserved LLC — Non-Emergency Medical Transportation in Pennsylvania" description="Safe, reliable, and compassionate non-emergency medical transportation across Pennsylvania. Book a ride or partner with CareDeserved LLC."><Home /></P>} />
            <Route path="/about" element={<P title="About Us — CareDeserved LLC" description="CareDeserved provides safe, reliable, and respectful transportation. Every passenger deserves dignity and compassionate service."><About /></P>} />
            <Route path="/services" element={<P title="Transportation Services — CareDeserved LLC" description="NEMT, wheelchair, ambulatory, ODP & community, private pay, and facility transportation services across Pennsylvania."><Services /></P>} />
            <Route path="/services/:slug" element={<P title="Transportation Service — CareDeserved LLC" description="Learn about CareDeserved's non-emergency medical and community transportation services."><ServiceDetail /></P>} />
            <Route path="/for-clients" element={<P title="For Clients — CareDeserved LLC" description="Learn how to arrange transportation with CareDeserved: broker, facility, private pay, and ODP/community options."><ForClients /></P>} />
            <Route path="/facilities" element={<P title="For Facilities & Partners — CareDeserved LLC" description="A dependable transportation partner for healthcare facilities, agencies, day programs, and community organizations."><ForFacilities /></P>} />
            <Route path="/service-areas" element={<P title="Service Areas — CareDeserved LLC" description="CareDeserved serves counties across eastern and central Pennsylvania with medical and community transportation."><ServiceAreas /></P>} />
            <Route path="/service-areas/:slug" element={<PublicLayout><ServiceAreaDetail /></PublicLayout>} />
            <Route path="/careers" element={<P title="Careers — CareDeserved LLC" description="Join the CareDeserved team. NEMT drivers, wheelchair van drivers, dispatch, and support staff roles available."><Careers /></P>} />
            <Route path="/careers/apply" element={<P title="Apply — Careers at CareDeserved LLC" description="Apply to CareDeserved. One smart application for Transportation/NEMT and ODP / Human Services roles across Pennsylvania."><JobApplication /></P>} />
            <Route path="/careers/:id" element={<P title="Job Opening — CareDeserved LLC" description="View this CareDeserved job opening and apply today."><JobDetail /></P>} />
            <Route path="/contact" element={<P title="Contact Us — CareDeserved LLC" description="Contact CareDeserved LLC. Call 484-877-5855 or email info@caredeserved.com."><Contact /></P>} />
            <Route path="/book-a-ride" element={<P title="Book a Ride — CareDeserved LLC" description="Request non-emergency medical transportation with CareDeserved. Complete the form and we'll confirm availability."><BookRide /></P>} />
            <Route path="/private-pay" element={<P title="Private Pay Transportation — CareDeserved LLC" description="Request a private pay transportation quote for reliable rides outside of broker-arranged trips."><PrivatePay /></P>} />
            <Route path="/odp-services" element={<P title="ODP Services in PA — Home & Community Habilitation, Companion, Registered Nursing & Transportation | CareDeserved LLC" description="CareDeserved LLC is a qualified Pennsylvania ODP provider offering Home & Community Habilitation, Companion Services, Registered Nursing, and Non-Emergency Transportation. Now accepting ODP referrals."><OdpServices /></P>} />
            <Route path="/privacy-policy" element={<P title="Privacy Policy — CareDeserved LLC"><Legal page="privacy-policy" /></P>} />
            <Route path="/terms-of-use" element={<P title="Terms of Use — CareDeserved LLC"><Legal page="terms-of-use" /></P>} />
            <Route path="/accessibility" element={<P title="Accessibility — CareDeserved LLC"><Legal page="accessibility" /></P>} />

            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Overview />} />
              <Route path="follow-ups" element={<FollowUps />} />
              <Route path="ride-requests" element={<Leads />} />
              <Route path="quotes" element={<Leads />} />
              <Route path="facility-leads" element={<Leads />} />
              <Route path="contacts" element={<Leads />} />
              <Route path="applications" element={<Leads />} />
              <Route path="job-openings" element={<JobOpenings />} />
              <Route path="odp-referrals" element={<Leads />} />
              <Route path="testimonials" element={<TestimonialsAdmin />} />
              <Route path="service-areas" element={<ServiceAreasAdmin />} />
              <Route path="media" element={<MediaLibrary />} />
              <Route path="announcements" element={<Announcements />} />
              <Route path="content" element={<SiteContent />} />
            </Route>
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
        </SiteProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
